// LUCKY DUCKY · PAYTABLE / INFO modal.
//
// Player-facing information panel with four tabs: PAYS · PAYLINES ·
// BETO RESPINS · RULES.  Everything renders from the SAME authoritative
// configuration the engine loads (spec/verified_paytable.json via
// engine/paytable.js and spec/cardinal_paylines.json via engine/paylines.js)
// so the UI can never silently diverge from the math.  Opening/closing this
// modal is pure display state — it never spins, changes wager/balance, or
// touches feature/RNG state.
import React, { useEffect, useState } from 'react';
import {
  STANDARD_LINE_PAYS, DOUBLE_WILD_TABLE, BONUS_SCATTER_TABLE,
  WAGERS_CREDITS, DENOMINATION_CENTS, RULES,
} from './engine/paytable';
import { PAYLINES_20, REEL_COUNT, ROW_COUNT } from './engine/paylines';
import { SYMBOLS } from './engine/symbols';

const TABS = ['PAYS', 'PAYLINES', 'BETO RESPINS', 'RULES'];

// symbol-id → paytable pay-key + display name, in descending pay order.
const PAY_ROWS = [
  { symId: 'WILD',     name: 'Double Wild',    table: DOUBLE_WILD_TABLE,        note: 'Subs all except Bonus' },
  { symId: 'RED_7',    name: 'Red 7',          table: STANDARD_LINE_PAYS.RED_7 },
  { symId: 'B3R',      name: 'Triple BAR',     table: STANDARD_LINE_PAYS.B3R },
  { symId: 'B2R',      name: 'Double BAR',     table: STANDARD_LINE_PAYS.B2R },
  { symId: 'B1R',      name: 'Single BAR',     table: STANDARD_LINE_PAYS.B1R },
  { symId: 'BELL',     name: 'Bell',           table: STANDARD_LINE_PAYS.BELL },
  { symId: 'PLUM',     name: 'Plum',           table: STANDARD_LINE_PAYS.PLUM },
  { symId: 'WATERMEL', name: 'Watermelon',     table: STANDARD_LINE_PAYS.WATERMELON },
  { symId: 'ORANGE',   name: 'Orange',         table: STANDARD_LINE_PAYS.ORANGE },
  { symId: 'CHERRY',   name: 'Cherries',       table: STANDARD_LINE_PAYS.CHERRIES },
  { symId: 'BONUS',    name: 'Bonus',          table: BONUS_SCATTER_TABLE,      note: 'Scatter — pays anywhere' },
].filter(r => r.table);

const tierKeys = (table) => Object.keys(table).map(Number).sort((a, b) => a - b);

const BASE_WAGER = WAGERS_CREDITS.PLAY_1X;

// Convert a base-wager credit pay into the dollar amount actually paid at the
// player's current bet (wins scale linearly with bet, then the server payout
// factor is applied). Matches the credited win the player receives.
const payDollars = (baseCredits, betCredits, factor) => {
  const scaled = baseCredits * (betCredits / BASE_WAGER) * factor;
  return `$${((scaled * DENOMINATION_CENTS) / 100).toFixed(2)}`;
};

function PayRow({ row, betCredits, factor }) {
  const sym = SYMBOLS[row.symId];
  return (
    <div className="ld-pt-row" data-testid={`ld-pay-row-${row.symId}`}>
      <div className="ld-pt-sym">
        {sym && <img src={sym.asset} alt={row.name} draggable={false} />}
        <span className="ld-pt-sym-name">{row.name}</span>
        {row.note && <span className="ld-pt-sym-note">{row.note}</span>}
      </div>
      <div className="ld-pt-tiers">
        {tierKeys(row.table).map(k => (
          <span className="ld-pt-tier" key={k}>
            <b>{k}×</b>
            <em>{payDollars(row.table[String(k)], betCredits, factor)}</em>
          </span>
        ))}
      </div>
    </div>
  );
}

function LineDiagram({ line }) {
  return (
    <div className="ld-pt-line" data-testid={`ld-payline-${line.id}`}>
      <div className="ld-pt-line-id">{line.id}</div>
      <div className="ld-pt-mini" style={{ gridTemplateColumns: `repeat(${REEL_COUNT}, 1fr)` }}>
        {Array.from({ length: ROW_COUNT }).map((_, row) =>
          Array.from({ length: REEL_COUNT }).map((_, reel) => (
            <span
              key={`${reel}-${row}`}
              className={`ld-pt-cell${line.rows[reel] === row ? ' on' : ''}`}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function PaytableModal({ open, initialTab = 'PAYS', betCredits = WAGERS_CREDITS.PLAY_1X, payoutFactor = 1, onClose }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);
  if (!open) return null;

  const betDollars = `$${((betCredits * DENOMINATION_CENTS) / 100).toFixed(2)}`;
  const wagerLine = Object.entries(WAGERS_CREDITS)
    .map(([, v]) => `$${((v * DENOMINATION_CENTS) / 100).toFixed(2)}`)
    .join(' · ');

  const lineGroups = [[1, 5], [6, 10], [11, 15], [16, 20]];

  return (
    <div className="ld-pt-modal" data-testid="ld-paytable-modal" onClick={onClose}>
      <div className="ld-pt-card" onClick={e => e.stopPropagation()}>
        <div className="ld-pt-head">
          <h2>LUCKY DUCKY · PAYTABLE</h2>
          <button className="ld-pt-close" onClick={onClose} data-testid="ld-paytable-close" aria-label="Close">✕</button>
        </div>

        <div className="ld-pt-tabs" role="tablist">
          {TABS.map(t => (
            <button
              key={t}
              role="tab"
              className={`ld-pt-tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
              data-testid={`ld-paytable-tab-${t.replace(/\s+/g, '-').toLowerCase()}`}
            >{t}</button>
          ))}
        </div>

        <div className="ld-pt-body" data-testid={`ld-paytable-body-${tab.replace(/\s+/g, '-').toLowerCase()}`}>
          {tab === 'PAYS' && (
            <div className="ld-pt-pays">
              <p className="ld-pt-hint">Payouts for your <b>{betDollars}</b> bet — for 3 · 4 · 5 of a kind (Bonus 2 · 3 · 4 · 5). Higher bets pay proportionally more. Wins pay left-to-right from reel 1.</p>
              {PAY_ROWS.map(row => <PayRow key={row.symId} row={row} betCredits={betCredits} factor={payoutFactor} />)}
            </div>
          )}

          {tab === 'PAYLINES' && (
            <div className="ld-pt-lines">
              <p className="ld-pt-hint"><b>20 PAYLINES</b> — the exact Lucky Ducky line map. Highlighted cells show each line's path across the 5 reels.</p>
              {lineGroups.map(([from, to]) => (
                <div className="ld-pt-line-group" key={from}>
                  <div className="ld-pt-group-title">LINES {from}–{to}</div>
                  <div className="ld-pt-lines-grid">
                    {PAYLINES_20.filter(l => l.id >= from && l.id <= to).map(l => (
                      <LineDiagram key={l.id} line={l} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'BETO RESPINS' && (
            <div className="ld-pt-section">
              <div className="ld-pt-beto-hero">BETO RESPINS</div>
              <ul className="ld-pt-list">
                <li>Land <b>{RULES.min_bonus_to_award_free_plays}+ BONUS</b> ducks (scatter, anywhere on the reels) to trigger the feature.</li>
                <li>Triggering awards <b>{RULES.free_plays_awarded_on_trigger} FREE PLAYS</b>.</li>
                <li>Every win during BETO RESPINS pays <b>{RULES.feature_win_multiplier}×</b>.</li>
                <li><b>{RULES.min_bonus_to_enter_respin}+ BONUS</b> ducks award a <b>RESPIN FREE SPIN</b> for another shot at the trigger.</li>
                <li>BONUS is a scatter and pays on its own: {tierKeys(BONUS_SCATTER_TABLE).map(k => `${k}=${BONUS_SCATTER_TABLE[String(k)]}`).join(' · ')} credits.</li>
              </ul>
            </div>
          )}

          {tab === 'RULES' && (
            <div className="ld-pt-section">
              <ul className="ld-pt-list">
                <li>Wins are evaluated <b>left-to-right</b>, starting on reel 1. A gap breaks the win.</li>
                <li>All <b>20 paylines</b> are fixed and pay <b>independently</b>.</li>
                <li><b>Double Wild</b> substitutes for every symbol except <b>BONUS</b>. A winning line with 1 or 2 Wilds pays <b>2× the line win</b>.</li>
                <li><b>BONUS</b> is a scatter — it pays anywhere and is <b>never</b> substituted by a Wild.</li>
                <li>Bet options: {wagerLine}.</li>
                <li>Only the highest win per line is paid. Malfunction voids all plays and pays.</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
